import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  Stack,
  Button,
  List,
  SelectPicker,
  TagPicker,
  IconButton,
  Input,
  Message,
  useToaster
} from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { useAppDispatch } from '@/config/hooks';
import { getIndividualAccess, updateUserAccessYPriority } from '@/services/access.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

interface EditDrawerProps {
  open: boolean;
  onClose: () => void;
  data: any;
  floors: any[];
  zones: any[];
}

const EditDrawer = ({
  open,
  onClose,
  data,
  floors: rawFloors,
  zones: rawZones
}: EditDrawerProps) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();

  const [localAccessList, setLocalAccessList] = useState<any[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);

  const floors = useMemo(
    () =>
      rawFloors.map(floor => ({
        value: floor._id,
        label: floor.name
      })),
    [rawFloors]
  );

  const zones = useMemo(
    () =>
      rawZones.map(zone => ({
        value: zone._id,
        label: zone.name,
        floor: zone.floor
      })),
    [rawZones]
  );

  useEffect(() => {
    if (data?.Zones?.length) {
      const zonesByFloor = data.Zones.reduce((acc, zone) => {
        const zoneData = zones.find(z => z.value === zone._id);
        if (zoneData) {
          if (!acc[zoneData.floor]) {
            acc[zoneData.floor] = [];
          }
          acc[zoneData.floor].push(zone._id);
        }
        return acc;
      }, {});

      const accessList = Object.keys(zonesByFloor).map(floorId => ({
        floor: floorId,
        zones: zonesByFloor[floorId]
      }));

      if (JSON.stringify(localAccessList) !== JSON.stringify(accessList)) {
        setLocalAccessList(accessList);
      }

      const newSelectedZones = data.Zones.map(zone => zone._id);
      if (JSON.stringify(selectedZones) !== JSON.stringify(newSelectedZones)) {
        setSelectedZones(newSelectedZones);
      }
    }
  }, [data, zones]);

  const handleAddRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        floor: null,
        zones: []
      }
    ]);
  };

  const handleRemoveRow = (index: number) => {
    const values = [...localAccessList];
    const floorId = values[index]?.floor;
    const floorZones = zones.filter(zone => zone.floor === floorId);
    const newZones = selectedZones.filter(zoneId => !floorZones.some(z => z.value === zoneId));

    setSelectedZones(newZones);
    values.splice(index, 1);
    setLocalAccessList(values);
  };

  const handleChangeFloor = (index: number, floorId: string) => {
    const newArr = [...localAccessList];
    newArr[index].floor = floorId;
    newArr[index].zones = [];
    setLocalAccessList(newArr);
  };
  const { t } = useTranslation();

  const handleSubmit = async () => {
    try {
      await dispatch(
        updateUserAccessYPriority(data.User._id, {
          Zones: selectedZones
        })
      );
      await dispatch(getIndividualAccess());

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Individual access updated successfully')}
        </Message>,
        { placement: 'topCenter' }
      );

      onClose();
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message) || t('Error updating individual access')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Edit Individual Access')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>{t('User')}</Form.ControlLabel>
            <Input
              value={`${data?.User?.firstname || ''} ${data?.User?.lastname || ''}`}
              readOnly
            />
          </Form.Group>

          <Stack style={{ marginTop: 50, marginBottom: 10, marginLeft: 10 }} spacing={5}>
            <h6 className="title">{t('Access Zones')}</h6>
          </Stack>

          <div className="ml-10">
            <List hover>
              {localAccessList.map((item, index) => (
                <List.Item key={index} index={index + 1}>
                  <Stack justifyContent="space-around" wrap>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Floor')}</div>
                      <SelectPicker
                        value={item.floor}
                        onChange={value => handleChangeFloor(index, value)}
                        data={floors}
                        placement="auto"
                        style={{ width: 250 }}
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== index)
                          .map(item => item.floor)}
                      />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>Zones</div>
                      <TagPicker
                        data={zones.filter(zone => zone.floor === item.floor)}
                        value={selectedZones}
                        onChange={setSelectedZones}
                        style={{ width: 250 }}
                        placement="auto"
                      />
                    </div>
                    <IconButton
                      onClick={() => handleRemoveRow(index)}
                      icon={<MinusIcon />}
                      size="xs"
                    />
                  </Stack>
                </List.Item>
              ))}
            </List>
            <IconButton
              onClick={handleAddRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default EditDrawer;
